import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { SendOutlined, RightOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CorpsMessage from './CorpsMessage';
import discussionService, { EMBLEMES } from '../../services/discussionService';
import { getUser, CHARTE_COLORS as C } from '../../variables/constants';

/**
 * Une mention dépliée dans la cloche, avec réponse en ligne.
 *
 * C'EST L'ANTIDOTE AU COÛT DE LA RÉPONSE, et donc la pièce qui décide si l'outil vit.
 * Sans elle, répondre « ok » à cinq sollicitations dans la journée impose cinq
 * navigations vers cinq dossiers, puis cinq retours. Personne ne le fait deux jours
 * de suite. Ici : un clic pour acquitter, trois mots et Entrée pour répondre.
 *
 * La réponse part comme citation du message qui a mentionné - sinon, arrivée seule en
 * bas d'un fil, elle répondrait à une question que plus personne ne situe.
 */

const tempsRelatif = (iso, t) => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return t('mentionCloche.alInstant');
  if (min < 60) return t('mentionCloche.ilYAMinutes', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('mentionCloche.ilYAHeures', { n: h });
  const j = Math.floor(h / 24);
  return j === 1 ? t('mentionCloche.hier') : t('mentionCloche.ilYAJours', { n: j });
};

export default function MentionCloche({ mention, onOuvrirDossier, onRepondu }) {
  const { t } = useTranslation('commun');
  const [reponse, setReponse] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [acquittee, setAcquittee] = useState(null);
  const moi = getUser();

  const envoyer = async () => {
    const texte = reponse.trim();
    if (!texte) return;
    setEnvoi(true);
    try {
      await discussionService.ecrire(mention.entiteType, mention.entiteTrackingId, {
        corps: texte,
        reponseA: mention.messageTrackingId,
      });
      setReponse('');
      toast.success(t('mentionCloche.reponseEnvoyee'));
      onRepondu?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('mentionCloche.reponseImpossible'));
    } finally {
      setEnvoi(false);
    }
  };

  const reagir = async (code) => {
    try {
      await discussionService.basculerReaction(mention.messageTrackingId, code);
      // Retour visuel local : recharger la cloche entière pour une pastille
      // ferait clignoter la liste sous les yeux de l'agent.
      setAcquittee(code);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('mentionCloche.reactionImpossible'));
    }
  };

  return (
    <div style={{
      padding: '10px 12px', borderBottom: `1px solid ${C.border}`,
      background: mention.lu ? '#fff' : C.greenLight,
    }}>
      <div style={{ display: 'flex', gap: 9 }}>
        <span style={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0, marginTop: 2,
          background: C.greenMid, color: '#fff', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
        }}>
          {mention.auteur?.initiales || '?'}
        </span>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>
              {[mention.auteur?.prenoms, mention.auteur?.nom].filter(Boolean).join(' ')}
            </span>
            <span style={{ fontSize: 11.5, color: C.greenMid, fontWeight: 600 }}>
              {mention.entiteLibelle}
            </span>
            <span style={{ fontSize: 11, color: C.faint }}>{tempsRelatif(mention.date, t)}</span>
          </div>

          {mention.ancreLibelle && (
            <div style={{ fontSize: 11, color: '#3730a3', marginTop: 2 }}>
              ⌐ {mention.ancreLibelle}
            </div>
          )}

          <div style={{
            fontSize: 12.5, color: C.text, marginTop: 4, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            <CorpsMessage
              corps={mention.corps}
              mentions={[{ utilisateurTrackingId: moi?.utilisateurTrackingId }]}
              moiTrackingId={moi?.utilisateurTrackingId}
            />
          </div>

          {/* Acquitter d'un clic : la plupart des sollicitations n'appellent pas
              une phrase, et sans ce geste le fil se remplit de « ok ». */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 7, flexWrap: 'wrap' }}>
            {EMBLEMES.map((e) => (
              <Tooltip key={e.code} title={e.titre}>
                <span
                  onClick={() => reagir(e.code)}
                  style={{
                    width: 24, height: 24, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', borderRadius: 5, fontSize: 13,
                    border: `1px solid ${acquittee === e.code ? C.greenMid : 'transparent'}`,
                    background: acquittee === e.code ? '#fff' : 'transparent',
                  }}
                >
                  {e.glyphe}
                </span>
              </Tooltip>
            ))}
            <input
              value={reponse}
              onChange={(ev) => setReponse(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); envoyer(); } }}
              placeholder={t('mentionCloche.repondre')}
              maxLength={5000}
              style={{
                flex: 1, minWidth: 110, height: 26, fontSize: 12, padding: '0 8px',
                border: `1px solid ${C.border}`, borderRadius: 6, outline: 'none',
              }}
            />
            <Button
              type="text"
              size="small"
              icon={<SendOutlined style={{ fontSize: 12 }} />}
              loading={envoi}
              disabled={!reponse.trim()}
              onClick={envoyer}
              style={{ color: reponse.trim() ? C.greenMid : C.faint }}
            />
          </div>

          <div
            onClick={() => onOuvrirDossier?.(mention)}
            style={{ marginTop: 5, fontSize: 11.5, color: C.greenMid, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('mentionCloche.ouvrirDossier')} <RightOutlined style={{ fontSize: 9 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
